import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, postData, deleteData, activateData } from '../../../apiService';
import Link from 'react-router-dom/Link';
import Info from '@mui/icons-material/Info';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PrintIcon from '@mui/icons-material/Print';
import { Box } from '@mui/material';
import QAPrint from './Print';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import JoinRightIcon from '@mui/icons-material/JoinRight';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import { generateRevisionQuotationPDF } from "./RevisionPDF";
import { generateRevisionQuotationExcel } from "./generateRevisionQuotationExcel";
import DescriptionIcon from '@mui/icons-material/Description';

function RevisionQuotationIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
        fetchRevisionTableData();
    }, [isAuthenticated, companyId, branchId]);

    // Auto-refresh data when fromDate or toDate changes
    useEffect(() => {
        if (fromDate && toDate && isAuthenticated) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // Function to fetch table data and set state
    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        fetchData('RevisionApi', `/${companyId}/${branchId}/${fromDate}/${toDate}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Customer Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const handleDelete = async () => {
        deleteData('QuotationApi', `${deleteItemId}`, token)//, token
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('WorkApi', `${itemId}`)//, token
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handlePrint = async (QaId) => {
        try {
            const response = await fetchData('QuotationApi', `/QAPrint/${QaId}`, token); //, token
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <QAPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing QA print :', error);
        }
    };

    const [isModalOpen, setIsModalOpen] = useState(false);

    // Revision Details 
    const [revisionData, setRevisionData] = useState([]);
    const fetchRevisionTableData = async (id) => {
        setRevisionData([]);
        setLoading(true);
        fetchData('RevisionApi', `/QuotationRevision/${id}`, token) //, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setRevisionData(response.response_data);
                    setIsModalOpen(!isModalOpen);
                }
            })
            .catch((error) => {
                console.error('Error fetching Quotation Revision Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    // Revision Modal
    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    // QA convert to Invoice
    const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');

    const [formData, setFormData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    const InvoiceConfirmModel = () => {
        setInvoiceModalOpen(!isInvoiceModalOpen);
    };

    const handleConvertInvoice = async (QuotationId) => {
        setLoading(true);
        // Set current date as default
        const currentDate = new Date().toISOString().split('T')[0];
        setInvoiceDate(currentDate);
        
        fetchData('QuotationApi', `/Quotation/${QuotationId}`, token) //, token
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Quotation);
                    setSelectedItems(response.response_data.QuotationDetails || []);
                    setFormData(prevData => ({
                        ...prevData,
                    }));
                    setInvoiceModalOpen(!isInvoiceModalOpen);
                }
            })
            .catch((error) => {
                console.error('Error fetching Sale Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    }

    const handleConfirmInvoice = async (QuotationId) => {
        // Validate inputs
        if (!invoiceNumber || !invoiceDate) {
            alert("Please enter both Invoice Number and Invoice Date");
            return;
        }

        setIsLoading(true);

        const invoiceVM = {
            Invoice: {
                ...formData,
                Id: 0,
                QuotationId: QuotationId,
                CustomerId: formData.CustomerId,
                InvoiceNo: invoiceNumber,
                InvoiceDate: invoiceDate,
                CreatedBy: userId,
                CompanyId: companyId,
            },
            InvoiceDetails: selectedItems,
        };

        try {
            await postData("InvoiceApi", invoiceVM, token);
            //window.location.reload();
            alert("Successfully Invoice Generated");
            setInvoiceModalOpen(false);
            setInvoiceNumber('');
            setInvoiceDate('');
            fetchTableData();

        } catch (error) {
            console.error("Error creating Invoice:", error);
            setErrorMessage("Something went wrong while saving!");
        } finally {
            setIsLoading(false);
        }
    };

    // Revision to Invoice 
    const [isRevisionInvoiceModalOpen, setIsRevisionInvoiceModalOpen] = useState(false);
    const handleConvertRevisionInvoice = async (QuotationId) => {
        setLoading(true);
        // Set current date as default
        const currentDate = new Date().toISOString().split('T')[0];
        setInvoiceDate(currentDate);
        
        fetchData('QuotationApi', `/Quotation/${QuotationId}`, token) //, token
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Quotation);
                    setSelectedItems(response.response_data.QuotationDetails || []);
                    setFormData(prevData => ({
                        ...prevData,
                    }));
                    setInvoiceModalOpen(!isInvoiceModalOpen);
                }
            })
            .catch((error) => {
                console.error('Error fetching Quotation Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    }

    const handleConfirmRevisionInvoice = async (QuotationId) => {
        // Validate inputs
        if (!invoiceNumber || !invoiceDate) {
            alert("Please enter both Invoice Number and Invoice Date");
            return;
        }

        setIsLoading(true);

        const invoiceVM = {
            Invoice: {
                ...formData,
                Id: 0,
                QuotationId,
                CustomerId: formData.CustomerId,
                InvoiceNo: invoiceNumber,
                InvoiceDate: invoiceDate,
                CreatedBy: userId,
                CompanyId: companyId,
            },
            InvoiceDetails: selectedItems,
        };

        try {
            await postData("InvoiceApi", invoiceVM, token);
            alert("Successfully Invoice Generated");

            // Refresh data without reloading the page
            await fetchRevisionTableData(QuotationId);
            await fetchTableData();

            // Close all modals and reset inputs
            setIsRevisionInvoiceModalOpen(false);
            setInvoiceModalOpen(false);
            setIsModalOpen(false);
            setInvoiceNumber('');
            setInvoiceDate('');

        } catch (error) {
            console.error("Error creating Revision to Invoice:", error);
            setErrorMessage("Something went wrong while saving!");
        } finally {
            setIsLoading(false);
        }
    };

    // --- styles ---
    const headerStyle = {
        background: 'linear-gradient(to right, #111110ff, #111110ff)',
        color: 'white',
        fontWeight: '600',
        fontSize: '12px',
        border: '1px solid #ccc',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        whiteSpace: 'nowrap',
        padding: '2px',
        borderRadius: '4px',
        textTransform: 'uppercase'
    };

    const cellStyle = {
        padding: '2px',
        border: '1px solid #f8d7da',
        fontSize: '12px'
    };

    const handleGenerate = async (QaId) => {
        try {
            const response = await fetchData('QuotationApi', `/QAPrint/${QaId}`, token);
            const labelData = response.response_data;

            if (labelData) {
                generateRevisionQuotationPDF(labelData);
            } else {
                console.error("No quotation data received.");
            }
        } catch (error) {
            console.error("Error generating quotation PDF:", error);
        }
    };

    const handleGenerateExcel = async (QaId) => {
        try {
            const response = await fetchData('QuotationApi', `/QAPrint/${QaId}`, token);
            const labelData = response.response_data;
            
            console.log('Revision Excel - Full response:', response);
            console.log('Revision Excel - labelData:', labelData);
            console.log('Revision Excel - QAPrintItems:', labelData?.QAPrintItems);

            if (labelData) {
                generateRevisionQuotationExcel(labelData);
            } else {
                console.error("No quotation data received.");
            }
        } catch (error) {
            console.error("Error generating Revision Excel:", error);
        }
    };

    return (
        <div className="container mt-2 mx-auto" style={{ maxWidth: '100%' }}>

            {/* <div className='w-full mb-2 flex'> */}

            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 50,
                    }}
                >
                    {/* Inline Diamond Loader */}
                    <div
                        style={{
                            position: 'relative',
                            width: '50px',
                            height: '50px',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            transform: 'rotate(45deg)',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            boxShadow: '0 6px 18px rgba(255,255,255,0.2)',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                inset: '6px',
                                margin: 'auto',
                                background: '#ffffff',
                                borderRadius: '4px',
                            }}
                        ></div>
                        <div
                            style={{
                                position: 'absolute',
                                inset: '-12px',
                                margin: 'auto',
                                background: '#ff66cc',
                                borderRadius: '6px',
                                animation: 'diamondLoader 2s linear infinite',
                            }}
                        ></div>
                        <style>
                            {`
            @keyframes diamondLoader {
              0%,10% { transform: translate(-50px,-50px) rotate(-45deg); }
              90%,100% { transform: translate(0px,0px) rotate(-45deg); }
            }
          `}
                        </style>
                    </div>
                </div>
            )}

            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px", marginTop: '-20px' }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Revision Quotation
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

                {/* From Date */}
                <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* To Date */}
                <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* Refresh Button */}
                <button
                    onClick={fetchTableData}
                    style={{
                        background: "#1090a3",
                        color: "white",
                        fontSize: "12px",
                        border: "none",
                        borderRadius: "20px",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}
                >
                    Refresh
                </button>
                {/* {hasCreateMasterPermission && (
                    <Tooltip title="Create New Quotation" arrow>
                        <Button
                            component={Link}
                            to="/QuotationCreate"
                            style={{
                                background: "#3F4F44",
                                color: "white",
                                fontSize: "12px",
                                border: "none",
                                borderRadius: "20px",
                                padding: "4px 14px",
                                cursor: "pointer",
                                transition: "0.2s",
                                fontFamily: 'inherit'
                            }}
                        >
                            Create
                        </Button>
                    </Tooltip>
                )} */}
            </div>
            <div className="watermark" >
                <CopyrightIcon /><b style={{ fontFamily: 'serif' }}>EFFTABS SOLUTIONS LLP</b>
            </div>
            {/* </div> */}

            <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto', marginTop: '-10px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#0f172a" }}> {/* Dark navy-blue */}
                                {[
                                    "Sl.No",
                                    "REV QA Date",
                                    "REV QA No",
                                    "Name",
                                    "GST No",
                                    "Taxable(₹)",
                                    "GST(₹)",
                                    "Total(₹)",
                                    "Invoice Status",
                                    ""
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            borderBottom: "2px solid #1e293b",
                                            color: "#f8fafc", // soft white
                                            padding: "10px 12px",
                                            textAlign: ["Taxable(₹)", "GST(₹)", "Total(₹)"].includes(head)
                                                ? "right"
                                                : "left",
                                            letterSpacing: "0.5px",
                                            backgroundColor: "#0f172a", // Dark navy-blue
                                            padding: '2px',

                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredData && filteredData.map((item, index) => (
                                <TableRow
                                    key={item.Id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#F1E7E7' },
                                        transition: 'background-color 0.3s ease-in-out'
                                    }}
                                >
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '2%', fontSize: '12px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', textAlign: 'center', width: '10%', fontSize: '12px' }}>
                                        {item.QuotationDate ? formatDate(item.QuotationDate) : "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '10%',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <Link
                                            to={`/QuotationRevisionDetails/${item.Id}`}
                                            style={{
                                                textDecoration: "none",
                                                color: "#1976d2",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item.RevisionNumber}
                                        </Link>
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '35%', fontSize: '12px' }}>{item.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>{item.GSTNumber}</TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.TaxableAmount ? item.TaxableAmount.toFixed(2) : "0.00"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', fontSize: '12px' }}>
                                        {(
                                            (item.CGSTAmount || 0) +
                                            (item.SGSTAmount || 0) +
                                            (item.IGSTAmount || 0)
                                        ).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            textAlign: 'left',
                                            border: '1px solid #f8d7da',
                                            width: '10%',
                                            fontSize: '12px',
                                            backgroundColor:
                                                item.InvoiceStatus === 'Active Invoice'
                                                    ? '#d4edda' // light green
                                                    : item.InvoiceStatus === 'Cancelled Invoice'
                                                        ? '#f8d7da' // light red
                                                        : '#fff3cd', // light yellow for Not Created
                                            color:
                                                item.InvoiceStatus === 'Active Invoice'
                                                    ? 'green'
                                                    : item.InvoiceStatus === 'Cancelled Invoice'
                                                        ? 'red'
                                                        : 'orangered',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {item.InvoiceStatus}
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '10%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: '4px',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            <Tooltip title="Revision Quotation Print" arrow>
                                                <IconButton size="small" onClick={() => handleGenerate(item.Id)}>
                                                    <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Export Revision to Excel" arrow>
                                                <IconButton size="small" onClick={() => handleGenerateExcel(item.Id)}>
                                                    <DescriptionIcon sx={{ fontSize: 18, color: 'green' }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Revision Quotation Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/QuotationRevisionDetails/${item.Id}`}
                                                >
                                                    <Info sx={{ fontSize: 20, color: 'coral' }} />
                                                </IconButton>
                                            </Tooltip>

                                            {item.IsInvoiced == false && (
                                                <Tooltip title="Revision Edit Quotation" arrow>
                                                    <IconButton
                                                        size="small"
                                                        component={Link}
                                                        to={`/RevisionQuotationEdit/${item.Id}`}
                                                        color="primary"
                                                    >
                                                        <EditIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            <Tooltip title={item.IsActive ? "Delete Revision Quotation" : "Activate Revision Quotation"} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)
                                                    }
                                                    color="error"
                                                >
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>

                                            {item.IsInvoiced == false && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        gap: '4px',
                                                        flexWrap: 'nowrap',
                                                        marginLeft: '5px'
                                                    }}
                                                >
                                                    <Tooltip title="Convert To Invoice" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleConvertInvoice(item.Id)}
                                                            color="primary"
                                                        >
                                                            <ReceiptLongIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>

                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                                    Cancel
                                </button>
                                <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                                    Confirm
                                </button>
                            </DialogActions>
                        </Dialog>
                    </Table>
                    {!loading && data.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '50px', color: '#5d2929ff' }}>
                            No Data Available
                        </div>
                    )}

                    {!loading && data.length > 0 && (
                        <div>
                            {/* Render your table or content here */}
                        </div>
                    )}
                </TableContainer>
            </div>

            {
                isModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-opacity-30 z-10">
                        <div
                            className="bg-white p-2 rounded-xl shadow-2xl relative border border-gray-200"
                            style={{
                                width: '70%',
                                maxWidth: '800px',
                                maxHeight: '80vh',
                                overflowY: 'auto',
                            }}
                        >
                            {/* Close Button */}
                            <Tooltip title="Close Previous Revision">
                                <IconButton
                                    onClick={() => setIsModalOpen(false)}
                                    sx={{ position: 'absolute', top: 10, right: 10, zIndex: 20 }}
                                    size="small"
                                    aria-label="close"
                                >
                                    <CloseIcon fontSize="small" sx={{ color: 'darkred' }} />
                                </IconButton>
                            </Tooltip>

                            {/* Title */}
                            <h6
                                style={{
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    color: '#4C063C',
                                    textAlign: 'center',
                                    fontSize: '13px',
                                    marginBottom: '10px'
                                }}
                            >
                                Previous Revision
                            </h6>

                            {Array.isArray(revisionData) && revisionData.length > 0 ? (
                                <>
                                    {/* Info Section */}
                                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 mb-3">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                            <p>
                                                <span className="font-semibold text-gray-700">QA No: </span>
                                                <span className="text-gray-900">{revisionData[0].QuotationNumber}</span>
                                            </p>
                                            <p>
                                                <span className="font-semibold text-gray-700">Customer: </span>
                                                <span className="text-gray-900">{revisionData[0].CustomerName}</span>
                                            </p>
                                            <p>
                                                <span className="font-semibold text-gray-700">GST No: </span>
                                                <span className="text-gray-900">{revisionData[0].GSTNumber}</span>
                                            </p>
                                        </div>
                                    </div>

                                    {/* Table Section */}
                                    <TableContainer component={Paper} sx={{ borderRadius: '10px' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={headerStyle} width="3%">Sl.No</TableCell>
                                                    <TableCell style={headerStyle} width="6%">QA Date</TableCell>
                                                    <TableCell style={headerStyle} width="10%">QA Rev No</TableCell>
                                                    <TableCell style={headerStyle} width="10%">Taxable(₹)</TableCell>
                                                    <TableCell style={headerStyle} width="7%">CGST(₹)</TableCell>
                                                    <TableCell style={headerStyle} width="7%">SGST(₹)</TableCell>
                                                    <TableCell style={headerStyle} width="7%">IGST(₹)</TableCell>
                                                    <TableCell style={headerStyle} width="10%">Total(₹)</TableCell>
                                                    <TableCell style={headerStyle} width="5%"></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {revisionData.map((item, index) => (
                                                    <TableRow
                                                        key={item.Id}
                                                        sx={{
                                                            '&:hover': { backgroundColor: '#F9F6F6' },
                                                            transition: 'background-color 0.2s ease-in-out'
                                                        }}
                                                    >
                                                        <TableCell sx={cellStyle}>{index + 1}</TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'center' }}>
                                                            {item.QuotationDate ? formatDate(item.QuotationDate) : "-"}
                                                        </TableCell>
                                                        <TableCell sx={cellStyle}>
                                                            <Link
                                                                to={`/QuotationRevisionDetails/${item.Id}`}
                                                                style={{ color: '#27445D', fontWeight: 'bold' }}
                                                            >
                                                                {item.RevisionNumber}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>
                                                            {item.TaxableAmount ? item.TaxableAmount.toFixed(2) : "0.00"}
                                                        </TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>
                                                            {item.CGSTAmount ? item.CGSTAmount.toFixed(2) : "0.00"}
                                                        </TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>
                                                            {item.SGSTAmount ? item.SGSTAmount.toFixed(2) : "0.00"}
                                                        </TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>
                                                            {item.IGSTAmount ? item.IGSTAmount.toFixed(2) : "0.00"}
                                                        </TableCell>
                                                        <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>
                                                            {item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}
                                                        </TableCell>
                                                        <TableCell sx={cellStyle}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                                                                {item.IsInvoiced == false && (
                                                                    <Tooltip title="Convert To Invoice" arrow>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleConvertRevisionInvoice(item.Id)}
                                                                            color="primary"
                                                                        >
                                                                            <ReceiptLongIcon sx={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                {/* <Tooltip title="Quotation Print" arrow>
                                                                <IconButton size="small" onClick={() => handlePrint(item.Id)}>
                                                                    <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                                </IconButton>
                                                            </Tooltip> */}
                                                                <Tooltip title="Quotation Print" arrow>
                                                                    <IconButton size="small" onClick={() => handleGenerate(item.Id)}>
                                                                        <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : (
                                <div className="p-6 text-center text-gray-600 font-semibold border rounded-lg shadow-md mt-4">
                                    🚫 No Revisions Available
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {
                isInvoiceModalOpen && (
                    <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                        <div className="fixed inset-0 flex items-center justify-center z-10 bg-black/50">
                            <div className="bg-white p-6 rounded-lg shadow-lg relative" style={{ width: '70%', maxHeight: '90vh', overflowY: 'auto' }}>
                                {/* Close Button */}
                                <Tooltip title="Close Invoice">
                                    <IconButton
                                        onClick={() => setInvoiceModalOpen(false)}
                                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 20 }}
                                        size="small"
                                        aria-label="close"
                                    >
                                        <CloseIcon fontSize="small" sx={{ color: 'darkred' }} />
                                    </IconButton>
                                </Tooltip>

                                <h6 style={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#4C063C', textAlign: 'center', fontSize: '13px', marginBottom: '15px' }}>Confirm Invoice</h6>
                                
                                <div style={{ padding: '12px', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '15px' }}>
                                    {/* First Row: QA No and GST No */}
                                    <div style={{ display: 'flex', gap: '40px', marginBottom: '10px', fontSize: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>QA No: </span>
                                            <span style={{ color: '#111827' }}>{formData.QuotationNumber}</span>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>GST No: </span>
                                            <span style={{ color: '#111827' }}>{formData.GSTNumber}</span>
                                        </div>
                                    </div>

                                    {/* Second Row: Customer */}
                                    <div style={{ marginBottom: '15px', fontSize: '12px' }}>
                                        <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Customer: </span>
                                        <span style={{ color: '#111827' }}>{formData.CustomerName}</span>
                                    </div>

                                    {/* Third Row: Invoice No and Invoice Date */}
                                    <div style={{ display: 'flex', gap: '20px', maxWidth: '500px' }}>
                                        {/* Invoice Number Input */}
                                        <div style={{ width: '240px' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', color: '#374151' }}>
                                                Invoice No <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={invoiceNumber}
                                                onChange={(e) => setInvoiceNumber(e.target.value)}
                                                placeholder="Enter Invoice Number"
                                                style={{
                                                    border: "1px solid #d1d5db",
                                                    borderRadius: "4px",
                                                    fontSize: "12px",
                                                    height: "32px",
                                                    width: "100%",
                                                    padding: "0 8px"
                                                }}
                                            />
                                        </div>

                                        {/* Invoice Date Input */}
                                        <div style={{ width: '180px' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', color: '#374151' }}>
                                                Invoice Date <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={invoiceDate}
                                                onChange={(e) => setInvoiceDate(e.target.value)}
                                                style={{
                                                    border: "1px solid #d1d5db",
                                                    borderRadius: "4px",
                                                    fontSize: "12px",
                                                    height: "32px",
                                                    width: "100%",
                                                    padding: "0 8px"
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Items List Table */}
                                <div style={{ marginBottom: '15px' }}>
                                    <h6 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase', color: '#374151' }}>Items List</h6>
                                    <TableContainer component={Paper} sx={{ maxHeight: 250, border: "1px solid #ddd" }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px' }}>Sl.No</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px' }}>Description</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'center' }}>QTY</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'right' }}>Rate</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'right' }}>Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedItems && selectedItems.length > 0 ? (
                                                    selectedItems.map((item, index) => (
                                                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px' }}>{index + 1}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px' }}>
                                                                <span style={{ color: 'blue' }}>{item.DetailsName}</span> / {item.WorkDetailsName}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'center' }}>{item.Quantity}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'right' }}>{parseFloat(item.Rate || 0).toFixed(2)}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'right' }}>{parseFloat(item.Amount || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ textAlign: 'center', padding: '16px', fontStyle: 'italic' }}>
                                                            No items found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>

                                <div className="flex mt-4" style={{ justifyContent: 'center' }}>
                                    {/* Submit Button */}
                                    <button
                                        onClick={() => handleConfirmInvoice(formData.Id)}
                                        disabled={isLoading}
                                        style={{
                                            backgroundColor: '#b35e15ff',
                                            color: '#ffffff',
                                            padding: '6px 12px',
                                            fontSize: '13px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            transition: 'transform 0.15s ease, background-color 0.2s ease',
                                            opacity: isLoading ? 0.6 : 1,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isLoading) {
                                                e.target.style.transform = 'scale(1.04)';
                                                e.target.style.backgroundColor = '#b06a0dff';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isLoading) {
                                                e.target.style.transform = 'scale(1)';
                                                e.target.style.backgroundColor = '#b35e15ff';
                                            }
                                        }}
                                    >
                                        <DownloadDoneIcon fontSize="small" sx={{ color: 'white' }} /><span style={{ fontSize: '13px' }}>Submit</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                isRevisionInvoiceModalOpen && (
                    <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                        <div className="fixed inset-0 flex items-center justify-center z-10 bg-black/50">
                            <div className="bg-white p-6 rounded-lg shadow-lg relative" style={{ width: '70%', maxHeight: '90vh', overflowY: 'auto' }}>
                                {/* Close Button */}
                                <Tooltip title="Close Invoice">
                                    <IconButton
                                        onClick={() => setIsRevisionInvoiceModalOpen(false)}
                                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 20 }}
                                        size="small"
                                        aria-label="close"
                                    >
                                        <CloseIcon fontSize="small" sx={{ color: 'darkred' }} />
                                    </IconButton>
                                </Tooltip>

                                <h6 style={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#4C063C', textAlign: 'center', fontSize: '13px', marginBottom: '15px' }}>Confirm Invoice</h6>
                                
                                <div style={{ padding: '12px', borderRadius: '6px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '15px' }}>
                                    {/* First Row: QA No and GST No */}
                                    <div style={{ display: 'flex', gap: '40px', marginBottom: '10px', fontSize: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>QA No: </span>
                                            <span style={{ color: '#111827' }}>{formData.QuotationNumber}</span>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>GST No: </span>
                                            <span style={{ color: '#111827' }}>{formData.GSTNumber}</span>
                                        </div>
                                    </div>

                                    {/* Second Row: Customer */}
                                    <div style={{ marginBottom: '15px', fontSize: '12px' }}>
                                        <span style={{ fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>Customer: </span>
                                        <span style={{ color: '#111827' }}>{formData.CustomerName}</span>
                                    </div>

                                    {/* Third Row: Invoice No and Invoice Date */}
                                    <div style={{ display: 'flex', gap: '20px', maxWidth: '500px' }}>
                                        {/* Invoice Number Input */}
                                        <div style={{ width: '240px' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', color: '#374151' }}>
                                                Invoice No <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={invoiceNumber}
                                                onChange={(e) => setInvoiceNumber(e.target.value)}
                                                placeholder="Enter Invoice Number"
                                                style={{
                                                    border: "1px solid #d1d5db",
                                                    borderRadius: "4px",
                                                    fontSize: "12px",
                                                    height: "32px",
                                                    width: "100%",
                                                    padding: "0 8px"
                                                }}
                                            />
                                        </div>

                                        {/* Invoice Date Input */}
                                        <div style={{ width: '180px' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', color: '#374151' }}>
                                                Invoice Date <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={invoiceDate}
                                                onChange={(e) => setInvoiceDate(e.target.value)}
                                                style={{
                                                    border: "1px solid #d1d5db",
                                                    borderRadius: "4px",
                                                    fontSize: "12px",
                                                    height: "32px",
                                                    width: "100%",
                                                    padding: "0 8px"
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Items List Table */}
                                <div style={{ marginBottom: '15px' }}>
                                    <h6 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase', color: '#374151' }}>Items List</h6>
                                    <TableContainer component={Paper} sx={{ maxHeight: 250, border: "1px solid #ddd" }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px' }}>Sl.No</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px' }}>Description</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'center' }}>QTY</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'right' }}>Rate</TableCell>
                                                    <TableCell sx={{ backgroundColor: '#1F2A38', color: 'white', fontWeight: 'bold', fontSize: '11px', padding: '6px', textAlign: 'right' }}>Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedItems && selectedItems.length > 0 ? (
                                                    selectedItems.map((item, index) => (
                                                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px' }}>{index + 1}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px' }}>
                                                                <span style={{ color: 'blue' }}>{item.DetailsName}</span> / {item.WorkDetailsName}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'center' }}>{item.Quantity}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'right' }}>{parseFloat(item.Rate || 0).toFixed(2)}</TableCell>
                                                            <TableCell sx={{ fontSize: '11px', padding: '6px', textAlign: 'right' }}>{parseFloat(item.Amount || 0).toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ textAlign: 'center', padding: '16px', fontStyle: 'italic' }}>
                                                            No items found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>

                                <div className="flex mt-4" style={{ justifyContent: 'center' }}>
                                    {/* Submit Button */}
                                    <button
                                        onClick={() => handleConfirmRevisionInvoice(formData.Id)}
                                        disabled={isLoading}
                                        style={{
                                            backgroundColor: '#b35e15ff',
                                            color: '#ffffff',
                                            padding: '6px 12px',
                                            fontSize: '13px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            transition: 'transform 0.15s ease, background-color 0.2s ease',
                                            opacity: isLoading ? 0.6 : 1,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isLoading) {
                                                e.target.style.transform = 'scale(1.04)';
                                                e.target.style.backgroundColor = '#b06a0dff';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isLoading) {
                                                e.target.style.transform = 'scale(1)';
                                                e.target.style.backgroundColor = '#b35e15ff';
                                            }
                                        }}
                                    >
                                        <DownloadDoneIcon fontSize="small" sx={{ color: 'white' }} /><span style={{ fontSize: '13px' }}>Submit</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

        </div >

    );
}

export default RevisionQuotationIndex;

